import Component from "@glimmer/component";
import { service } from "@ember/service";
import { arg } from "glesys-controlpanel/utils/arg";
import { func, instanceOf, string } from "prop-types";
import ProjectModel from "glesys-controlpanel/models/project";
import { all, keepLatestTask, restartableTask, task } from "ember-concurrency";
import { tracked } from "@glimmer/tracking";
import { action } from "@ember/object";
import { query } from "@warp-drive/legacy/compat/builders";

export default class ServersCreateIpSelectionBox extends Component {
  @service intl;
  @service store;

  @arg(instanceOf(ProjectModel).isRequired) project;
  @arg(string.isRequired) platform;
  @arg(string.isRequired) dataCenter;
  @arg(func.isRequired) onChange;

  @tracked availableIps = [];
  @tracked reservedIps = [];

  @tracked selectedIpV4;
  @tracked selectedIpV6;

  get reservedIpsV4() {
    return this.reservedIps.filter((ip) => ip.version === 4);
  }

  get reservedIpsV6() {
    return this.reservedIps.filter((ip) => ip.version === 6);
  }

  get availableIpsV4() {
    return this.availableIps.filter((ip) => ip.version === 4);
  }

  get availableIpsV6() {
    return this.availableIps.filter((ip) => ip.version === 6);
  }

  get selectableIpsV4() {
    return [{ id: "none" }, ...this.reservedIpsV4, ...this.availableIpsV4];
  }

  get selectableIpsV6() {
    return [{ id: "none" }, ...this.reservedIpsV6, ...this.availableIpsV6];
  }

  get selectableIpsV4Options() {
    let quarantineText = this.intl.t("label.ip-quarantined").toString();

    let noIpOption = [{ label: this.intl.t("label.no-ipv4").toString(), value: "none" }];
    let ips = [
      {
        label: this.intl.t("label.reserved-ips"),
        options: this.reservedIpsV4.map((ip) => ({
          value: ip.id,
          disabled: ip.isquarantined,
          title: ip.isquarantined ? quarantineText : "",
        })),
      },
      {
        label: this.intl.t("label.available-ips"),
        options: this.availableIpsV4.map((ip) => ({ value: ip.id })),
      },
    ].filter((group) => group.options.length);

    return noIpOption.concat(ips);
  }

  get selectableIpsV6Options() {
    let quarantineText = this.intl.t("label.ip-quarantined").toString();

    let noIpOption = [{ label: this.intl.t("label.no-ipv6").toString(), value: "none" }];
    let ips = [
      {
        label: this.intl.t("label.reserved-ips"),
        options: this.reservedIpsV6.map((ip) => ({
          value: ip.id,
          disabled: ip.isquarantined,
          title: ip.isquarantined ? quarantineText : "",
        })),
      },
      {
        label: this.intl.t("label.available-ips"),
        options: this.availableIpsV6.map((ip) => ({ value: ip.id })),
      },
    ].filter((group) => group.options.length);

    return noIpOption.concat(ips);
  }

  fetchAvailableIpsTask = task(async () => {
    return all(
      [4, 6].map((version) => {
        return this.store
          .request(
            query("available-ip", {
              projectkey: this.project.id,
              platform: this.platform,
              datacenter: this.dataCenter,
              ipversion: version,
            }),
          )
          .then((response) => response.content);
      }),
    );
  });

  setAvailableIpsTask = restartableTask(async () => {
    let availableIps = await this.fetchAvailableIpsTask.perform();
    this.availableIps = [].concat(...availableIps);
  });

  setDefaultIpAddressTask = keepLatestTask(async (version) => {
    let availableIps = this.availableIps.filter((ip) => ip.version === version);
    let reservedIps = this.reservedIps.filter((ip) => ip.version === version);

    let defaultIp;

    if (reservedIps.length) {
      defaultIp = reservedIps.find((ip) => !ip.isquarantined);
    }

    if (!defaultIp) {
      defaultIp = availableIps[Math.floor(Math.random() * availableIps.length)];
    }

    if (version === 4) {
      this.selectedIpV4 = defaultIp;
    } else {
      this.selectedIpV6 = defaultIp;
    }
  });

  setIpsTask = restartableTask(async () => {
    await all([this.setAvailableIpsTask.perform(), this.setReservedIpsTask.perform()]);
    await all([this.setDefaultIpAddressTask.perform(4), this.setDefaultIpAddressTask.perform(6)]);

    this.onChange(this.selectedIpV4, this.selectedIpV6);
  });

  setReservedIpsTask = restartableTask(async () => {
    let reservedIps = await this.project.reservedIps;

    this.reservedIps = reservedIps.filter(
      (ip) => ip.dataCenter === this.dataCenter && ip.platforms.includes(this.platform) && ip.serverId === null,
    );
  });

  loadData = task(async () => {
    this.selectedIpV4 = null;
    this.selectedIpV6 = null;

    return await this.setIpsTask.perform();
  });

  @action
  setIpV4(event) {
    this.selectedIpV4 = this.selectableIpsV4.find((ip) => ip.id === event.target.value);
    this.onChange(this.selectedIpV4, this.selectedIpV6);
  }

  @action
  setIpV6(event) {
    this.selectedIpV6 = this.selectableIpsV6.find((ip) => ip.id === event.target.value);
    this.onChange(this.selectedIpV4, this.selectedIpV6);
  }
}
